import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, CheckSquare, Square, Search } from "lucide-react";
import { RoleSelect, TempPasswordCard } from "./AddStaffDialog";

export default function StaffCaseloadDialog({ staff, consumers, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", credentials: "", role: "pp" });
  const [picked, setPicked] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [temp, setTemp] = useState(null);

  useEffect(() => {
    if (staff) {
      setForm({ name: staff.name || "", credentials: staff.credentials || "", role: staff.role || "pp" });
      setPicked(consumers.filter((c) => (c.assigned_staff || []).includes(staff.id)).map((c) => c.id));
      setTemp(null);
      setQ("");
    }
  }, [staff, consumers]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const shown = consumers.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.member_id || "").toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/staff/${staff.id}`, form);
      const { data } = await api.put(`/users/staff/${staff.id}/caseload`, { consumer_ids: picked });
      toast.success(`${form.name} now has ${data.assigned_count} consumer${data.assigned_count === 1 ? "" : "s"}.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      const { data } = await api.post(`/users/staff/${staff.id}/reset-password`);
      setTemp(data.temp_password);
      toast.success("Temporary password created.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-caseload-dialog" className="sm:max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{staff?.name}</DialogTitle>
          <DialogDescription>{staff?.email} · edit their details and tap every consumer on their caseload.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Full name</Label>
            <Input data-testid="caseload-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Credentials</Label>
            <Input data-testid="caseload-credentials-input" value={form.credentials} onChange={(e) => setForm((f) => ({ ...f, credentials: e.target.value }))} className="h-11 bg-[#F9F9F6] border-[#E2DFD8]" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Role</Label>
          <RoleSelect value={form.role} onChange={(r) => setForm((f) => ({ ...f, role: r }))} testid="caseload-role" />
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between gap-3 mb-2">
            <Label className="text-[10px] font-semibold text-[#7A8C82] uppercase tracking-wider">Caseload · {picked.length} of {consumers.length}</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#7A8C82]" strokeWidth={1.5} />
              <Input data-testid="caseload-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a consumer" className="h-9 pl-8 w-48 bg-white border-[#E2DFD8] text-sm" />
            </div>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
            {shown.length === 0 && <p className="text-sm text-[#7A8C82] py-3">No consumers match.</p>}
            {shown.map((c) => {
              const on = picked.includes(c.id);
              return (
                <button key={c.id} type="button" data-testid={`caseload-consumer-${c.id}`} onClick={() => toggle(c.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-colors ${on ? "bg-[#EAF1EC] border-[#4A6B53]" : "bg-white border-[#E2DFD8] hover:border-[#4A6B53]"}`}>
                  {on ? <CheckSquare className="w-5 h-5 text-[#4A6B53] shrink-0" strokeWidth={1.5} /> : <Square className="w-5 h-5 text-[#B8C4BC] shrink-0" strokeWidth={1.5} />}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-[#121814] truncate">{c.name}</span>
                    <span className="block text-xs text-[#7A8C82]">{c.member_id || "No ID"}{c.assigned_staff_names?.length ? ` · also: ${c.assigned_staff_names.filter((n) => n !== staff?.name).join(", ") || "—"}` : " · unassigned"}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {temp && <TempPasswordCard email={staff?.email} password={temp} />}

        <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
          <Button data-testid="reset-staff-password-btn" variant="outline" onClick={resetPassword} disabled={resetting} className="rounded-full border-[#E2DFD8] bg-white h-11">
            {resetting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} />} Reset password
          </Button>
          <Button data-testid="save-caseload-btn" onClick={save} disabled={saving || !form.name.trim()} className="rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white h-11 px-6">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />} Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
